import React, { useRef, useState } from 'react';
import ReactDatepicker from 'react-datepicker';
import cn from 'classnames';
import { isToday } from 'date-fns';

import { Icons } from '@icons/icons';

import { IDatepickerProps } from './Datepicker.types';

import { weekDayShortNames } from './constants';

import Button from '../Button';
import Label from '../Label';
import Meta from '../Meta';
import CustomHeader from './CustomHeader';

import 'react-datepicker/dist/react-datepicker.css';
import './Datepicker.scss';

const Datepicker: React.FC<IDatepickerProps> = ({
	className,
	containerClassName,
	disableFutureDates,
	disableInput = false,
	disabled,
	errorMessage,
	isError = false,
	excludeDates,
	handleChange,
	highlightDates,
	isClearable = false,
	isLeftPlaceholder = false,
	isLoading,
	label,
	maxDate,
	minDate,
	placeholderText = 'DD/MM/YYYY',
	required,
	selected,
	isMobileDevice = false,
	excludeDateIntervals,
	includeDateIntervals,
	includeDates,
	isTodayButton = true,
	isFullWidth = false,
	onCalendarClose,
	onCalendarOpen,
	renderDayContents,
	isHiddenErrorMessage = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isOpenedByIcon, setIsOpenedByIcon] = useState(false);

	const handleOpen = () => setIsOpen(true);
	const handleClose = () => setIsOpen(false);

	const calendarRef = useRef<any>();

	const onChange = (date: Date | null) => {
		handleChange(new Date(date as Date).getTime() as any);
		handleClose();
	};

	const clearHandler = () => {
		if (!selected) return handleOpen();

		onChange(null);
	};

	const getDayClassName = (date: Date) => {
		if (isToday(date)) return 'f5gDatepickerToday';

		return '';
	};

	const handleClickIcon = () => {
		if (disabled) return;

		if (isOpenedByIcon) {
			setIsOpen(false);
			setIsOpenedByIcon(false);
		} else {
			setIsOpen(true);
			setIsOpenedByIcon(true);
		}
	};

	return (
		<div className={cn(containerClassName, 'f5gDatepickerWrapper', {
			'f5gDatepickerError': !!errorMessage || isError,
			'f5gDatepickerErrorIcon': !!errorMessage || isError,
			'f5gDatepickerLeftPlaceholder': isLeftPlaceholder,
			'f5gDatepickerHiddenCaret': disableInput,
		})}>
			{label && (
				<Label
					className={cn('f5gDatepickerLabel', { 'f5gDatepickerLeft': isLeftPlaceholder })}
					label={label}
					required={required}
				/>
			)}
			<div className={cn('f5gDatepickerContainer', { 'f5gDatepickerInputDisabled': disabled })}>
				<ReactDatepicker
					ref={calendarRef}
					className={cn('f5gDatepicker', className, { 'fullWidth': isFullWidth })}
					dateFormat='dd/MM/yyyy'
					dayClassName={getDayClassName}
					disabled={disabled}
					excludeDateIntervals={excludeDateIntervals}
					excludeDates={excludeDates}
					formatWeekDay={(day) => (weekDayShortNames as any)[day]}
					highlightDates={highlightDates}
					includeDateIntervals={includeDateIntervals}
					includeDates={includeDates}
					maxDate={disableFutureDates && !maxDate ? new Date() : maxDate}
					minDate={minDate}
					onCalendarClose={onCalendarClose}
					onCalendarOpen={onCalendarOpen}
					onChange={onChange}
					onClickOutside={handleClose}
					onInputClick={handleOpen}
					onKeyDown={(e) => {
						if (disableInput) e.preventDefault();
					}}
					open={isOpen}
					placeholderText={placeholderText}
					portalId={isMobileDevice ? 'datepicker-portal-mobile' : 'datepicker-portal'}
					renderCustomHeader={(props) => <CustomHeader {...props} />}
					renderDayContents={renderDayContents}
					selected={selected}
					selectsStart
					todayButton={isTodayButton && (
						<Button
							className='f5gDatepickerTodayButton'
							text='Today'
							withoutBackground
						/>
					)}
					withPortal={isMobileDevice}
					wrapperClassName='wrapper'
				/>
				{!isLoading && !isClearable && (
					<span onClick={handleClickIcon}>{disabled ? Icons.calendarDisabled() : Icons.calendar()}</span>
				)}
				{isClearable
					&& <div
						className='f5gDatepickerClickable'
						onClick={clearHandler}
					>
						{!selected
							? Icons.calendar()
							: Icons.clearMultiselect()
						}
					</div>
				}
			</div>
			{errorMessage && !isHiddenErrorMessage && (
				<Meta
					className='f5gDatepickerMeta'
					message={errorMessage as string}
				/>
			)}
		</div>
	);
};

export default Datepicker;
